import { useState, useCallback } from "react";

/**
 * Hook che incapsula lo stato delle quantità ordinate per ogni articolo
 * della fermata corrente e tutti i setter granulari (qty, omaggio, nota,
 * sconto override).
 *
 * Estratto da GiriOrdineRapido.jsx per ridurne la dimensione e poter
 * riusare la stessa logica anche fuori dai Giri se necessario.
 *
 * Forma di `orderItems`: {
 *   [cod_articolo]: { qty: number, qty_om: number, note: string,
 *                     discount_override?: { sc1, sc2, sc3, sc4 } }
 * }
 */
export function useGiroOrderItems() {
  const [orderItems, setOrderItems] = useState({});
  // Codice dell'articolo appena attivato (selezionato/aggiunto). Usato per
  // l'auto-focus della cella qty (anche dopo un remount tra sezioni della
  // lista). Si resetta a null al prossimo toggle/reset.
  const [lastActivatedCod, setLastActivatedCod] = useState(null);

  const reset = useCallback(() => {
    setOrderItems({});
    setLastActivatedCod(null);
  }, []);

  const hasUnsaved = useCallback(
    () => Object.values(orderItems).some((e) => (e?.qty || 0) > 0 || (e?.qty_om || 0) > 0),
    [orderItems]
  );

  const toggle = useCallback((cod, defaultQty) => {
    setOrderItems((prev) => {
      const next = { ...prev };
      if (next[cod] !== undefined) {
        delete next[cod];
        // Disattivazione: nessuna nuova attivazione da focusare
        setLastActivatedCod((curr) => (curr === cod ? null : curr));
      } else {
        next[cod] = { qty: defaultQty || 1, qty_om: 0, note: "" };
        setLastActivatedCod(cod);
      }
      return next;
    });
  }, []);

  // Permette al figlio di "consumare" l'attivazione una volta focusato il qty.
  const consumeLastActivated = useCallback(() => setLastActivatedCod(null), []);

  const updateQty = useCallback((cod, qty) => {
    setOrderItems((prev) => ({ ...prev, [cod]: { ...(prev[cod] || { qty_om: 0 }), qty } }));
  }, []);

  const updateOmaggio = useCallback((cod, qty_om) => {
    setOrderItems((prev) => {
      const existing = prev[cod];
      if (existing !== undefined) {
        return { ...prev, [cod]: { ...existing, qty_om: Math.max(0, qty_om) } };
      }
      if (qty_om > 0) {
        return { ...prev, [cod]: { qty: 0, qty_om: Math.max(0, qty_om), note: "" } };
      }
      return prev;
    });
  }, []);

  const updateNote = useCallback((cod, note) => {
    setOrderItems((prev) => {
      const existing = prev[cod];
      if (!existing) return prev;
      return { ...prev, [cod]: { ...existing, note } };
    });
  }, []);

  const updateDiscount = useCallback((cod, override) => {
    setOrderItems((prev) => {
      const existing = prev[cod];
      if (!existing) return prev;
      if (override === null) {
        const { discount_override, ...rest } = existing;
        return { ...prev, [cod]: rest };
      }
      return { ...prev, [cod]: { ...existing, discount_override: override } };
    });
  }, []);

  return {
    orderItems,
    setOrderItems,
    lastActivatedCod,
    consumeLastActivated,
    reset,
    hasUnsaved,
    toggle,
    updateQty,
    updateOmaggio,
    updateNote,
    updateDiscount,
  };
}
